import { useState } from "react";

type Layout = { order: string[]; widths: Record<string, number> };

const storageKey = (id: string) => `promobile.table.${id}`;

const readLayout = (id?: string): Layout => {
  if (!id) return { order: [], widths: {} };
  try {
    const saved = JSON.parse(
      window.localStorage.getItem(storageKey(id)) || "null",
    );
    return {
      order: Array.isArray(saved?.order) ? saved.order : [],
      widths:
        saved?.widths && typeof saved.widths === "object" ? saved.widths : {},
    };
  } catch {
    return { order: [], widths: {} };
  }
};

const writeLayout = (id: string | undefined, layout: Layout) => {
  if (!id) return;
  try {
    window.localStorage.setItem(storageKey(id), JSON.stringify(layout));
  } catch {
    // the layout just won't survive a reload
  }
};

/**
 * Column order and widths the user set by dragging the headers, kept per
 * table across reloads. `keys` is the default order of the columns shown now.
 * The same hook as in the admin panel's DataTable.
 */
export const useColumnLayout = (id: string | undefined, keys: string[]) => {
  const [layout, setLayout] = useState<Layout>(() => readLayout(id));

  const update = (change: (prev: Layout) => Layout) =>
    setLayout((prev) => {
      const next = change(prev);
      writeLayout(id, next);
      return next;
    });

  // The saved order first; a column the save does not know yet (new, or
  // hidden when the order was saved) goes right after the column it follows
  // by default.
  const order = layout.order.filter((key) => keys.includes(key));
  keys.forEach((key, index) => {
    if (!order.includes(key))
      order.splice(
        index === 0 ? 0 : order.indexOf(keys[index - 1]) + 1,
        0,
        key,
      );
  });

  /** Puts `key` where `target` is now. */
  const reorder = (key: string, target: string) => {
    const from = order.indexOf(key);
    const to = order.indexOf(target);
    if (from < 0 || to < 0 || from === to) return;
    const next = [...order];
    next.splice(from, 1);
    next.splice(to, 0, key);
    update((prev) => ({ ...prev, order: next }));
  };

  /** A null width goes back to the default one. */
  const resize = (key: string, width: number | null) =>
    update((prev) => {
      const widths = { ...prev.widths };
      if (width == null) delete widths[key];
      else widths[key] = width;
      return { ...prev, widths };
    });

  return { order, widths: layout.widths, reorder, resize };
};

export default useColumnLayout;
